import Cookies from 'js-cookie';
import { nanoid } from 'nanoid';
import { ProcessedInvoice } from '../../types';

interface InvoicePayload {
  userId: string;
  invoices: Array<{
    invoiceId: string;
    name: string;
    iban: string;
    amount: number;
    currency: string;
    reference?: string;
  }>;
}

interface APIResponse {
  success: boolean;
  remainingQuota: number;
}

export async function sendInvoicesToAPI(
  processedInvoices: ProcessedInvoice[],
  onQuotaUpdate?: (quota: number) => void
): Promise<void> {
  const userId = Cookies.get('auth_uuid');
  if (!userId) {
    console.error('No auth_uuid cookie found');
    throw new Error('User not authenticated');
  }

  const successfulInvoices = processedInvoices.filter(invoice => invoice.status === 'success');
  if (successfulInvoices.length === 0) {
    console.log('No successful invoices to send');
    return;
  }

  console.log('Preparing payload for API', { 
    invoiceCount: successfulInvoices.length,
    userId 
  });

  const payload: InvoicePayload = {
    userId,
    invoices: successfulInvoices.map(invoice => ({
      invoiceId: `INV-${nanoid(8)}`,
      name: invoice.data.name,
      iban: invoice.data.iban,
      amount: invoice.data.amount,
      currency: invoice.data.currency || 'EUR',
      reference: invoice.data.reference
    }))
  };

  console.log('Sending invoices to API:', {
    endpoint: 'https://dashboard.invoice-qr.com/.netlify/functions/create-invoice',
    payload
  });

  try {
    const authCookie = Cookies.get('auth_uuid');

    const response = await fetch('https://dashboard.invoice-qr.com/.netlify/functions/create-invoice', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${authCookie}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API request failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`Failed to send invoices: ${errorText}`);
    }

    const responseData: APIResponse = await response.json();
    console.log('Successfully sent invoices to API:', responseData);

    // Update quota if callback provided
    if (onQuotaUpdate && typeof responseData.remainingQuota === 'number') {
      onQuotaUpdate(responseData.remainingQuota);
    }
  } catch (error) {
    console.error('Error sending invoices to API:', error);
    throw error;
  }
}